import React, { useState, useCallback } from "react";
import {
  useTranslate,
  Identifier,
  useDataProvider,
  FormTab,
} from "react-admin";
import { IDropDownAppData } from "../../../common/redux/reducers/appDataReducer";
import {
  Tooltip,
  IconButton,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  MenuList,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import HourglassEmptyRoundedIcon from "@material-ui/icons/HourglassEmptyRounded";
import { InnerFieldPermissionsTab } from "./field-permissions/InnerFieldPermissionsTab";
import { RoleDto } from "../../../common/data/service-proxies";
import resourceNames from "../../../common/data/resourceNames";
import { useForm } from "react-final-form";

interface PermissionsTabProps {
  disabled?: boolean;
  roles?: IDropDownAppData<string>[];
  groupedPermissionDefinitions?:
    | {
        [key: string]: {
          [key: string]: any;
        };
      }
    | null
    | undefined;
  isEdit?: boolean;
}

export const FieldPermissionsTab = ({
  roles,
  groupedPermissionDefinitions,
  isEdit,
  ...rest
}: PermissionsTabProps) => {
  const form = useForm();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [
    fieldPermissionsCopyAnchor,
    setFieldPermissionsCopyAnchor,
  ] = useState<HTMLButtonElement | null>(null);
  const [copying, setCopying] = useState<Identifier>();

  const isCopying = useCallback(
    (id?: Identifier | null) => {
      return copying === id;
    },
    [copying]
  );

  const handleFieldPermissionsCopyClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setFieldPermissionsCopyAnchor(event.currentTarget);
  };

  const handleFieldPermissionsCopyClose = () => {
    setFieldPermissionsCopyAnchor(null);
  };

  const copyFieldPermissionsFromRole = async (id: Identifier) => {
    setCopying(id);
    try {
      const { data } = await dataProvider.getOne(resourceNames.ADMIN_ROLES, {
        id,
      });
      const role = data as RoleDto;

      form.change("fieldPermissions", role.fieldPermissions);
    } finally {
      setCopying(undefined);
      handleFieldPermissionsCopyClose();
    }
  };

  const fieldPermissionsCopyOpen = Boolean(fieldPermissionsCopyAnchor);
  return (
    <>
      <FormTab
        {...rest}
        label={
          <div className="tab-title-wrapper">
            {translate("resources.admin-roles.labels.fieldPermissions")}
            {roles && roles.length > 0 && (
              <Tooltip
                title={`${translate(
                  "resources.admin-roles.labels.copyFieldPermissions"
                )} ${translate("resources.admin-roles.labels.anotherRole")}`}
              >
                <IconButton onClick={handleFieldPermissionsCopyClick}>
                  <FileCopyIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        }
      >
        {groupedPermissionDefinitions && (
          <InnerFieldPermissionsTab
            permissionDefinitions={groupedPermissionDefinitions}
            isEdit={isEdit}
          />
        )}
      </FormTab>
      <Popover
        open={fieldPermissionsCopyOpen}
        anchorEl={fieldPermissionsCopyAnchor}
        onClose={handleFieldPermissionsCopyClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        getContentAnchorEl={null}
      >
        <MenuList>
          {roles?.map((x) => (
            <MenuItem
              key={x.id}
              onClick={() => copyFieldPermissionsFromRole(x.id!)}
            >
              <ListItemIcon>
                {isCopying(x.id) ? (
                  <HourglassEmptyRoundedIcon />
                ) : (
                  <FileCopyIcon />
                )}
              </ListItemIcon>
              <ListItemText>
                {`${translate(
                  "resources.admin-roles.labels.copyFieldPermissions"
                )} ${x.name}`}
              </ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};
